import React, { lazy } from 'react';
import { LocationProvider } from '@reach/router';
import { PrismicPreviewProvider } from 'gatsby-plugin-prismic-previews';
import PropTypes from 'prop-types';
import { Provider } from '@components/Context';
import { NavProvider } from '@components/navigation/Context';
import { linkResolver } from './linkResolver';
import { RECAPTCHA_ID } from './src/constants';
import { extractLanguageFromUrl, getCountryCodeByLocale } from './src/utils/language';

export default function Wrapper({ element, pathname }) {
  // Instantiating store in `wrapRootElement` handler ensures:
  //  - there is fresh store for each SSR page
  //  - it will be called only once in browser, when React mounts
  const currentPathname = typeof window !== 'undefined' ? window?.location?.pathname : pathname;
  const country = getCountryCodeByLocale(extractLanguageFromUrl(currentPathname)?.locale);

  // Global CSS for reCAPTCHA - badge hidden per Google's terms compliance
  const recaptchaStyles = `
    /* Hide reCAPTCHA badge (requires disclosure text in privacy policy) */
    .grecaptcha-badge {
      display: none !important;
      visibility: hidden !important;
    }
    
    /* General styling for all reCAPTCHA containers */
    [id^="recaptcha-container"] {
      z-index: 1000 !important;
      visibility: hidden !important;
      min-width: 256px !important;
      min-height: 60px !important;
    }
    
    /* Show reCAPTCHA containers only when they have content */
    [id^="recaptcha-container"]:not(:empty) {
      visibility: visible !important;
    }
    
    /* Default global container positioning (for login scenarios) */
    #recaptcha-container {
      position: fixed !important;
      bottom: 20px !important;
      left: 20px !important;
    }

    #recaptcha-container-login {
      margin-bottom: 20px;
    }
    #recaptcha-container-login > div {
      display: flex;
      align-items: flex-end;
      flex-direction: column;
    }
  `;

  // Create and inject the style element (browser only) - prevent duplicates
  if (typeof document !== 'undefined') {
    const existingStyle = document.getElementById('recaptcha-styles');
    if (!existingStyle) {
      const styleElement = document.createElement('style');
      styleElement.id = 'recaptcha-styles';
      styleElement.type = 'text/css';
      styleElement.innerHTML = recaptchaStyles;
      document.head.appendChild(styleElement);
    }
  }

  return (
    <LocationProvider>
      <Provider country={country}>
        <NavProvider>
          <PrismicPreviewProvider
             repositoryConfigs={[
               {
                 repositoryName: 'ecommerce-website',
                 typePrefix: 'ecommerce',
                 componentResolver: {
                   page: lazy(() => import('@components/page/Page')),
                   product: lazy(() => import('@components/products/ProductPage'))
                 },
                 linkResolver
               }
             ]}
           >
            <div id={RECAPTCHA_ID} />
            {element}
          </PrismicPreviewProvider>
        </NavProvider>
      </Provider>
    </LocationProvider>
  );
}

Wrapper.propTypes = {
  element: PropTypes.element.isRequired,
  pathname: PropTypes.string.isRequired
};
